/**
 * Email de Confirmação de Transação
 * Template enviado após a confirmação de uma transação financeira (resgate de pontos, pagamento, etc.).
 */

import { montarBase } from './EmailBase';

export type TipoTransacao = 'resgate' | 'pagamento' | 'transferencia' | 'deposito' | 'estorno';

export interface DestinatarioResgate {
  nome: string;
  documento: string;
  chavePix?: string;
  tipoChavePix?: string;
  dadosBancarios?: {
    banco: string;
    agencia: string;
    conta: string;
    tipoConta: string;
  };
}

export interface EmailConfirmacaoTransacaoParams {
  nome: string;
  tipo: TipoTransacao;
  valor: number;
  moeda?: string;
  data: string;
  hora: string;
  idTransacao: string;
  descricao?: string;
  saldoAtual?: number;
  minutosExpiracao?: number;
  destinatario?: DestinatarioResgate;
}

const ICONES_TRANSACAO: Record<TipoTransacao, { icone: string; cor: string; bgCor: string; label: string }> = {
  resgate: { icone: '&#127873;', cor: '#16A34A', bgCor: '#DCFCE7', label: 'Resgate de Recompensa' },
  pagamento: { icone: '&#128179;', cor: '#2563EB', bgCor: '#DBEAFE', label: 'Pagamento Realizado' },
  transferencia: { icone: '&#128260;', cor: '#7C3AED', bgCor: '#EDE9FE', label: 'Transferência' },
  deposito: { icone: '&#128176;', cor: '#059669', bgCor: '#D1FAE5', label: 'Depósito Recebido' },
  estorno: { icone: '&#128260;', cor: '#DC2626', bgCor: '#FEE2E2', label: 'Estorno' },
};

export function gerarEmailConfirmacaoTransacao(params: EmailConfirmacaoTransacaoParams): string {
  const transacao = ICONES_TRANSACAO[params.tipo];
  const moeda = params.moeda ?? 'BRL';
  const valorFormatado = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: moeda,
  }).format(params.valor);

  const saldo = params.saldoAtual !== undefined
    ? `<tr>
          <td style="padding: 8px 0;">
            <span style="font-size: 13px; color: ${transacao.cor}; font-weight: 600;">Saldo Atual</span>
          </td>
          <td align="right" style="padding: 8px 0;">
            <span style="font-size: 13px; color: #1E293B; font-weight: 700;">${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda }).format(params.saldoAtual)}</span>
          </td>
        </tr>`
    : '';

  const contaId = params.idTransacao.length > 8 ? `...#${params.idTransacao.slice(-8)}` : `#${params.idTransacao}`;

  // Bloco de destinatário — aparece apenas em resgates quando há dados do destinatário
  let blocoDestinatario = '';
  if (params.tipo === 'resgate' && params.destinatario) {
    const dest = params.destinatario;
    blocoDestinatario = `
    <!-- Card do Destinatário -->
    <table width="100%" cellpadding="0" cellspacing="0" style="margin: 24px 0; background-color: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px;">
      <tr>
        <td style="padding: 20px 24px;">
          <table width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="padding-bottom: 12px; border-bottom: 1px solid #E2E8F0;">
                <table cellpadding="0" cellspacing="0">
                  <tr>
                    <td style="vertical-align: middle; padding-right: 10px;">
                      <span style="font-size: 22px;">&#128100;</span>
                    </td>
                    <td style="vertical-align: middle;">
                      <p style="margin: 0; font-size: 14px; font-weight: 700; color: #334155;">Dados do Destinatário</p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding-top: 12px;">
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">Nome</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #1E293B; font-weight: 600;">${dest.nome}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">CPF/CNPJ</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #1E293B; font-family: 'Courier New', monospace;">${dest.documento}</span>
                    </td>
                  </tr>
                  ${dest.chavePix ? `<tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">Chave PIX</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #1E293B;">${dest.tipoChavePix ? dest.tipoChavePix + ': ' : ''}${dest.chavePix}</span>
                    </td>
                  </tr>` : ''}
                  ${dest.dadosBancarios ? `<tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">Banco</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #1E293B;">${dest.dadosBancarios.banco}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">Agência</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #1E293B;">${dest.dadosBancarios.agencia}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">Conta</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid #E2E8F0;">
                      <span style="font-size: 13px; color: #1E293B;">${dest.dadosBancarios.conta} (${dest.dadosBancarios.tipoConta})</span>
                    </td>
                  </tr>` : ''}
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`;
  }

  const conteudo = `
    <p style="margin: 0 0 16px 0; font-size: 15px; color: #475569; line-height: 1.7;">
      Sua transação foi processada com sucesso. Confira os detalhes abaixo.
    </p>

    <!-- Card do Status -->
    <table width="100%" cellpadding="0" cellspacing="0" style="margin: 24px 0; background-color: ${transacao.bgCor}; border: 1px solid ${transacao.cor}33; border-radius: 10px;">
      <tr>
        <td style="padding: 20px 24px;">
          <table width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="padding-bottom: 12px; border-bottom: 1px solid ${transacao.cor}22;">
                <table cellpadding="0" cellspacing="0">
                  <tr>
                    <td style="vertical-align: middle; padding-right: 12px;">
                      <span style="font-size: 28px;">${transacao.icone}</span>
                    </td>
                    <td style="vertical-align: middle;">
                      <p style="margin: 0 0 2px 0; font-size: 14px; font-weight: 700; color: ${transacao.cor};">${transacao.label}</p>
                      <p style="margin: 0; font-size: 22px; font-weight: 800; color: #1E293B;">${valorFormatado}</p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding-top: 12px;">
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid ${transacao.cor}15;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">Data</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid ${transacao.cor}15;">
                      <span style="font-size: 13px; color: #1E293B;">${params.data} às ${params.hora}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid ${transacao.cor}15;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">ID da Transação</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid ${transacao.cor}15;">
                      <span style="font-size: 13px; color: #1E293B; font-family: 'Courier New', monospace;">${contaId}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid ${transacao.cor}15;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">Status</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid ${transacao.cor}15;">
                      <span style="display: inline-block; background-color: #DCFCE7; color: #16A34A; font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 12px;">Concluída</span>
                    </td>
                  </tr>
                  ${params.descricao ? `<tr>
                    <td style="padding: 6px 0; border-bottom: 1px solid ${transacao.cor}15;">
                      <span style="font-size: 13px; color: #64748B; font-weight: 600;">Descrição</span>
                    </td>
                    <td align="right" style="padding: 6px 0; border-bottom: 1px solid ${transacao.cor}15;">
                      <span style="font-size: 13px; color: #1E293B;">${params.descricao}</span>
                    </td>
                  </tr>` : ''}
                  ${saldo}
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>

    ${blocoDestinatario}

    <!-- Aviso de segurança -->
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-top: 20px; background-color: #F1F5F9; border-radius: 8px; padding: 16px 20px;">
      <tr>
        <td>
          <table cellpadding="0" cellspacing="0">
            <tr>
              <td style="vertical-align: top; padding-right: 10px;">
                <span style="font-size: 16px; color: #64748B;">&#128274;</span>
              </td>
              <td style="font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;">
                <p style="margin: 0 0 4px 0; font-size: 13px; font-weight: 700; color: #334155;">Segurança da Transação</p>
                <p style="margin: 0; font-size: 12px; color: #64748B; line-height: 1.5;">
                  Todas as transações são processadas em ambiente seguro e criptografado.
                  Se você não reconhece esta transação, entre em contato imediatamente com nosso suporte.
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;

  const prazo = params.minutosExpiracao
    ? `O comprovante desta transação estará disponível no seu extrato por até <strong>${params.minutosExpiracao} dias</strong>. Após esse período, entre em contato com o suporte para solicitar o histórico.`
    : undefined;

  return montarBase({
    titulo: 'Transação Confirmada',
    saudacao: `Olá, <strong style="color: #1E293B;">${params.nome}</strong>!`,
    conteudo,
    prazoExpiracao: prazo,
  });
}